/* Native Shopify prices and quantity rules; no client-side discount calculation. */
(() => {
  'use strict';
  const quantityValid = (quantity, existing, rule) => Number.isSafeInteger(quantity) && quantity >= 0 &&
    (quantity === 0 || (quantity + existing >= rule.min && (rule.max === null || quantity + existing <= rule.max) && (quantity + existing) % rule.increment === 0));
  const nextQuantity = (quantity, existing, rule, direction) => {
    if (direction < 0) {
      const total = Math.floor((existing + quantity - 1) / rule.increment) * rule.increment;
      return total < rule.min || total <= existing ? 0 : total - existing;
    }
    const total = Math.ceil(Math.max(rule.min, existing + quantity + 1) / rule.increment) * rule.increment;
    return rule.max !== null && total > rule.max ? quantity : total - existing;
  };
  const volumePrice = (base, breaks, total) => [...breaks].sort((a, b) => a.min - b.min).reduce((price, br) => total >= br.min ? br.formatted : price, base);
  const cartCounts = cart => {
    const counts = new Map();
    for (const item of cart.items || []) counts.set(String(item.variant_id), (counts.get(String(item.variant_id)) || 0) + item.quantity);
    return counts;
  };
  async function addOrder({items, cartUrl, addUrl, fetcher, rules}) {
    // Refresh quantities before the only mutation. Never retry a POST automatically:
    // Shopify may partially update a bag even when it responds with a 422.
    const preflight = await fetcher(cartUrl + '.js', {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}});
    if (!preflight.ok) return {kind: 'preflight-error'};
    const counts = cartCounts(await preflight.json());
    for (const item of items) if (!quantityValid(item.quantity, counts.get(String(item.id)) || 0, rules.get(String(item.id)))) return {kind: 'quantity-error', counts};
    const response = await fetcher(addUrl, {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({items})});
    return {kind: response.ok ? 'added' : 'uncertain'};
  }
  // Export only in the local validation runner, not as a browser global.
  if (typeof module !== 'undefined' && module.exports) module.exports = {quantityValid, nextQuantity, volumePrice, cartCounts, addOrder};
  if (typeof document === 'undefined') return;
  function init(root) {
    if (root.dataset.partnerInitialized) return;
    root.dataset.partnerInitialized = 'true';
    const rows = [...root.querySelectorAll('[data-product-row]')];
    const groups = [...root.querySelectorAll('[data-group]')];
    if (!groups.length) return;
    const buttons = [...root.querySelectorAll('[data-group-button]')];
    const search = root.querySelector('[data-search]');
    const status = root.querySelector('[data-status]');
    const add = root.querySelector('[data-add-selected]');
    const live = root.dataset.ordering === 'true';
    let busy = false, finished = false, currentGroup = root.dataset.startAll === 'true' ? '__all__' : groups[0].dataset.group;
    const rowData = row => {
      const option = row.querySelector('[data-variant]').selectedOptions[0];
      return {option, id: option.value, available: option.dataset.available === 'true', existing: Number(option.dataset.inCart) || 0,
        rule: {min: Number(option.dataset.min) || 1, max: option.dataset.max ? Number(option.dataset.max) : null, increment: Number(option.dataset.increment) || 1}};
    };
    function filter() {
      const term = (search?.value || '').trim().toLocaleLowerCase();
      let matches = 0;
      groups.forEach(group => {
        const active = currentGroup === '__all__' || group.dataset.group === currentGroup;
        const categoryMatch = (group.dataset.groupTitle || '').toLocaleLowerCase().includes(term);
        const groupRows = [...group.querySelectorAll('[data-product-row]')];
        groupRows.forEach(row => row.hidden = !(categoryMatch || row.dataset.searchText.toLocaleLowerCase().includes(term)));
        const visible = groupRows.filter(row => !row.hidden).length;
        group.hidden = !active || (currentGroup === '__all__' && term !== '' && visible === 0);
        if (active) matches += visible;
        const empty = group.querySelector('[data-no-results]');
        if (empty) empty.hidden = !groupRows.length || visible > 0 || currentGroup === '__all__';
      });
      const emptyAll = root.querySelector('[data-catalog-empty]');
      if (emptyAll) emptyAll.hidden = currentGroup !== '__all__' || term === '' || matches > 0;
      buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.groupButton === currentGroup)));
    }
    function selection() {
      const chosen = new Map(), rules = new Map();
      let valid = true;
      for (const row of rows) {
        const data = rowData(row), input = row.querySelector('[data-quantity]');
        const q = Number(input.value);
        const ok = input.value !== '' && quantityValid(q, data.existing, data.rule) && (q === 0 || data.available);
        input.setAttribute('aria-invalid', String(!ok));
        if (!ok) valid = false;
        if (q > 0 && ok) {chosen.set(data.id, q); rules.set(data.id, data.rule);}
      }
      return {valid, rules, items: [...chosen].map(([id, quantity]) => ({id, quantity})), total: [...chosen.values()].reduce((a, b) => a + b, 0)};
    }
    function update() {
      for (const row of rows) {
        const data = rowData(row), input = row.querySelector('[data-quantity]'), quantity = Number(input.value) || 0;
        input.disabled = !live || !data.available || busy || finished;
        row.querySelector('[data-decrease]').disabled = input.disabled || quantity <= 0;
        row.querySelector('[data-increase]').disabled = input.disabled || nextQuantity(quantity, data.existing, data.rule, 1) === quantity;
        row.querySelector('[data-variant]').disabled = busy || finished;
        row.querySelector('[data-unavailable]').hidden = data.available;
        const inBag = row.querySelector('[data-in-bag]');
        inBag.hidden = data.existing === 0; inBag.querySelector('span').textContent = data.existing;
        row.querySelector('[data-rules]').hidden = data.rule.min === 1 && data.rule.increment === 1 && data.rule.max === null;
        row.querySelector('[data-rule-min] b').textContent = data.rule.min;
        row.querySelector('[data-rule-increment] b').textContent = data.rule.increment;
        row.querySelector('[data-rule-max]').hidden = data.rule.max === null;
        row.querySelector('[data-rule-max] b').textContent = data.rule.max ?? '';
        if (live) {
          const breaks = JSON.parse(data.option.dataset.breaks || '[]');
          const total = data.existing + quantity;
          row.querySelector('[data-unit-price]').textContent = volumePrice(data.option.dataset.price, breaks, total);
          const cents = [...breaks].sort((a,b) => a.min - b.min).reduce((price, br) => total >= br.min ? Number(br.cents) : price, Number(data.option.dataset.priceCents));
          window.ELAAWholesalePrices?.update(row, cents, Number(data.option.dataset.compareCents), data.option.dataset.compareFormatted);
        }
        const img = row.querySelector('[data-product-image]');
        if (img && data.option.dataset.image) img.src = data.option.dataset.image;
        row.querySelectorAll('[data-product-link]').forEach(link => link.href = data.option.dataset.url);
      }
      const selected = selection();
      add.disabled = !live || busy || finished || !selected.valid || !selected.items.length;
      if (!busy && !finished) status.textContent = !selected.valid ? root.dataset.validation : selected.total ? root.dataset.selectedLabel + ' ' + selected.total : root.dataset.hint;
    }
    function syncQuantity(row) {
      const id = rowData(row).id, value = row.querySelector('[data-quantity]').value;
      rows.filter(other => rowData(other).id === id).forEach(other => other.querySelector('[data-quantity]').value = value);
      update();
    }
    rows.forEach(row => {
      const input = row.querySelector('[data-quantity]');
      input.addEventListener('input', () => syncQuantity(row));
      for (const [selector, direction] of [['[data-increase]', 1], ['[data-decrease]', -1]]) row.querySelector(selector).addEventListener('click', () => {
        const data = rowData(row); input.value = nextQuantity(Number(input.value) || 0, data.existing, data.rule, direction); syncQuantity(row);
      });
      row.querySelector('[data-variant]').addEventListener('change', () => {
        const same = rows.find(other => other !== row && rowData(other).id === rowData(row).id);
        input.value = same ? same.querySelector('[data-quantity]').value : 0; update();
      });
    });
    buttons.forEach(button => button.addEventListener('click', () => {currentGroup = button.dataset.groupButton; filter();}));
    search?.addEventListener('input', filter);
    add.addEventListener('click', async () => {
      if (!live || busy || finished) return;
      const selected = selection();
      if (!selected.valid || !selected.items.length) {update(); return;}
      busy = true; status.textContent = root.dataset.adding; update();
      try {
        const result = await addOrder({items: selected.items, rules: selected.rules, cartUrl: root.dataset.cartUrl, addUrl: root.dataset.addUrl, fetcher: window.fetch.bind(window)});
        if (result.kind === 'quantity-error') {
          rows.forEach(row => [...row.querySelector('[data-variant]').options].forEach(option => option.dataset.inCart = result.counts.get(option.value) || 0));
          busy = false; update(); status.textContent = root.dataset.validation; return;
        }
        finished = true; busy = false;
        if (result.kind === 'added') {
          rows.forEach(row => row.querySelector('[data-quantity]').value = 0);
          status.textContent = root.dataset.added;
        } else status.textContent = root.dataset.error;
      } catch (_) {
        // A dropped response does not prove that Shopify rejected the write.
        finished = true; busy = false; status.textContent = root.dataset.error;
      }
      update();
      root.querySelector('[data-bag-link]').hidden = false;
      root.querySelector('[data-refresh]').hidden = false;
      root.querySelector('[data-bag-link]').focus();
    });
    root.classList.add('ewp-enhanced');
    root.querySelector('[data-group-buttons]').hidden = false;
    const searchWrap = root.querySelector('[data-search-wrap]'); if (searchWrap) searchWrap.hidden = false;
    // Reload retains preview-theme parameters and refreshes context/availability.
    root.querySelector('[data-refresh]').addEventListener('click', event => {event.preventDefault(); window.location.reload();});
    filter(); update();
  }
  function scan(container) {if (container.matches?.('[data-partner-space]')) init(container); container.querySelectorAll('[data-partner-space]').forEach(init);}
  if (!window.elaaPartnerSpaceLoaded) {
    window.elaaPartnerSpaceLoaded = true;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document)); else scan(document);
    document.addEventListener('shopify:section:load', event => scan(event.target));
    document.addEventListener('shopify:block:select', event => {
      const group = event.target.closest?.('[data-group]');
      if (group) group.closest('[data-partner-space]')?.querySelector(`[data-group-button="${group.dataset.group}"]`)?.click();
    });
  }
})();
